import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

/**
 * Full-viewport preloader:
 *  - Rotating logo mark
 *  - "ऋ Design · Loading" text + numeric counter
 *  - After progress hits 100 → logo scales & fades, curtain lifts to reveal site
 */
export default function Preloader({ onDone }) {
  // Allow skipping the intro via ?nointro — useful for deep-links & previews
  const skipIntro =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("nointro");

  const [progress, setProgress] = useState(skipIntro ? 100 : 0);
  const [phase, setPhase] = useState("loading"); // loading → zooming → done
  const [visible, setVisible] = useState(!skipIntro);

  useEffect(() => {
    if (skipIntro) {
      onDone?.();
      return;
    }
    // Simulated progress — completes in ~2.4s
    const start = performance.now();
    const DURATION = 2400;
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / DURATION);
      // ease-out
      const eased = 1 - Math.pow(1 - p, 2.2);
      setProgress(Math.round(eased * 100));
      if (p < 1) raf = requestAnimationFrame(tick);
      else setPhase("zooming");
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [skipIntro, onDone]);

  useEffect(() => {
    if (phase !== "zooming") return;
    // Give the zoom animation ~1.2s, then unmount + notify parent
    const t = setTimeout(() => {
      setVisible(false);
      onDone?.();
    }, 1300);
    return () => clearTimeout(t);
  }, [phase, onDone]);

  // Lock scroll while preloader visible
  useEffect(() => {
    if (visible) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [visible]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="preloader"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } }}
          className="fixed inset-0 z-[100] bg-onyx text-cream overflow-hidden"
        >
          {/* Subtle vignette */}
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse 60% 40% at 50% 50%, rgba(176,140,74,0.12), transparent 70%)",
            }}
          />

          {/* Top-left: est. */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="absolute top-6 md:top-10 left-6 md:left-10 flex items-center gap-3 text-cream/60"
          >
            <span className="w-6 h-px bg-brass/70" />
            <span className="text-[10px] tracking-overline uppercase text-brass">
              ऋ Design · Est. 2020
            </span>
          </motion.div>

          {/* Top-right: date */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="absolute top-6 md:top-10 right-6 md:right-10 text-right text-[10px] tracking-overline uppercase text-cream/60"
          >
            New Delhi<br />
            <span className="text-brass">Studio No. 01</span>
          </motion.div>

          {/* Centre — rotating logo with circular "loading" text */}
          <div className="relative w-full h-full flex flex-col items-center justify-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={
                phase === "zooming"
                  ? { scale: 18, opacity: 0, transition: { duration: 1.25, ease: [0.7, 0, 0.84, 0] } }
                  : { opacity: 1, scale: 1, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } }
              }
              className="relative flex items-center justify-center"
            >
              {/* Rotating dashed ring */}
              <motion.div
                className="absolute inset-0 -m-14 md:-m-20 rounded-full border border-brass/40"
                style={{ borderStyle: "dashed" }}
                animate={{ rotate: 360 }}
                transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
              />
              {/* Rotating 'loading · loading · ...' ring */}
              <motion.div
                className="absolute inset-0 -m-10 md:-m-14 flex items-center justify-center"
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              >
                <svg
                  viewBox="0 0 300 300"
                  className="w-[calc(100%+64px)] h-[calc(100%+64px)] md:w-[calc(100%+112px)] md:h-[calc(100%+112px)]"
                >
                  <defs>
                    <path
                      id="preloader-arc"
                      d="M 150,150 m -130,0 a 130,130 0 1,1 260,0 a 130,130 0 1,1 -260,0"
                    />
                  </defs>
                  <text
                    className="fill-cream/70"
                    style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: "italic", fontSize: 12, letterSpacing: "0.42em" }}
                  >
                    <textPath href="#preloader-arc" startOffset="0%">
                      loading · loading · loading · loading · loading · loading · loading · loading ·
                    </textPath>
                  </text>
                </svg>
              </motion.div>

              {/* Spinning mark */}
              <motion.img
                src="/assets/logo-mark.png"
                alt="ऋ Design"
                animate={{ rotate: 360 }}
                transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
                className="w-24 h-24 md:w-32 md:h-32 object-contain select-none"
                draggable={false}
              />
            </motion.div>
          </div>

          {/* Bottom-left: numeric progress */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: phase === "loading" ? 1 : 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="absolute bottom-6 md:bottom-10 left-6 md:left-10 flex items-baseline gap-4"
          >
            <span
              className="font-serif italic text-cream tracking-editorial"
              style={{ fontSize: "clamp(56px, 8vw, 96px)", lineHeight: 1 }}
              data-testid="preloader-progress"
            >
              {String(progress).padStart(3, "0")}
            </span>
            <span className="text-[10px] tracking-overline uppercase text-cream/50 pb-2">
              / 100 · Curating
            </span>
          </motion.div>

          {/* Bottom-right: hint */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: phase === "loading" ? 1 : 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="absolute bottom-6 md:bottom-10 right-6 md:right-10 text-right text-[10px] tracking-overline uppercase text-cream/50"
          >
            The Luxury<br />
            <span className="text-brass">Design Concierge</span>
          </motion.div>

          {/* Bottom-center: thin progress bar */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: phase === "loading" ? 1 : 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="absolute left-1/2 -translate-x-1/2 bottom-6 md:bottom-14 w-40 md:w-56 h-px bg-cream/15"
          >
            <div
              className="h-full bg-brass transition-[width] duration-100 ease-linear"
              style={{ width: `${progress}%` }}
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
